import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, FileSpreadsheet, Download, Upload, RefreshCw, CheckCircle2,
  XCircle, AlertTriangle, Trash2, Send, Eye, ChevronLeft, ChevronRight,
  FileText, Loader2, ArrowRight
} from 'lucide-react';
import PortalLayout from './PortalLayout';
import { getGlobalPropertyId, setGlobalProperty } from './propertySync';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const portalApi = axios.create({ baseURL: API_URL });
const formatRM = (v) => `RM ${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PortalBookings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('manual');
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const limit = 20;

  // Excel Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Manual Booking state
  const [showManualForm, setShowManualForm] = useState(false);
  const [savingBooking, setSavingBooking] = useState(false);
  const [savedCustomers, setSavedCustomers] = useState([]);
  const [manualForm, setManualForm] = useState({
    buyer_type: 'individual', buyer_name: '', buyer_tin: '', buyer_id_type: 'NRIC',
    buyer_id_value: '', buyer_email: '', room_charge: '', tourism_tax: '', hotel_fee: ''
  });

  const token = localStorage.getItem('einvoice_token');
  const headers = { Authorization: `Bearer ${token}` };
  const [selectedPropertyId, setSelectedPropertyId] = useState(() => getGlobalPropertyId() || 'all');

  useEffect(() => {
    if (!token) { navigate('/einvoice/portal/auth'); return; }
    loadData();
    loadSavedCustomers();
    const h = (e) => {
      const prop = e.detail;
      setSelectedPropertyId(prop ? prop.id : 'all');
      // loadBookings will be called via the selectedPropertyId useEffect
    };
    window.addEventListener('property-changed', h);
    return () => window.removeEventListener('property-changed', h);
  }, []);

  useEffect(() => { loadBookings(); }, [page, selectedPropertyId]);

  const loadSavedCustomers = async () => {
    try {
      const { data } = await portalApi.get('/api/einvoice/portal/customers', { headers });
      setSavedCustomers(data.customers || []);
    } catch (e) { /* non-blocking */ }
  };

  const applyCustomer = (custId) => {
    const c = savedCustomers.find(x => x.id === custId);
    if (!c) return;
    setManualForm(f => ({
      ...f,
      buyer_type: c.buyer_type || 'individual',
      buyer_name: c.name || '',
      buyer_tin: c.tin || '',
      buyer_id_type: c.id_type || 'NRIC',
      buyer_id_value: c.id_value || '',
      buyer_email: c.email || '',
    }));
    toast.success(`Loaded ${c.name}`);
  };

  const handlePropertyChange = (val) => {
    setSelectedPropertyId(val);
    setPage(1);
    setGlobalProperty(val === 'all' ? null : val, properties);
  };

  const loadData = async () => {
    try {
      const { data } = await portalApi.get('/api/einvoice/portal/properties', { headers });
      setProperties(data.properties || []);
    } catch (e) { console.error(e); }
    await loadBookings();
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (selectedPropertyId && selectedPropertyId !== 'all') params.append('property_id', selectedPropertyId);
      const { data } = await portalApi.get(`/api/einvoice/gateway/bookings?${params}`, { headers });
      setBookings(data.bookings || []);
      setTotalBookings(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // ── Excel Upload Handlers ──

  const handleDownloadTemplate = async () => {
    try {
      const response = await portalApi.get('/api/einvoice/gateway/bookings/download-template', {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'staymii-booking-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) { toast.error('Failed to download template'); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Please select an Excel file (.xlsx)');
        return;
      }
      setUploadFile(file);
      setParsedData(null);
    }
  };

  const handleParseFile = async () => {
    if (!uploadFile) { toast.error('Please select a file first'); return; }
    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const { data } = await portalApi.post('/api/einvoice/gateway/bookings/parse-excel', formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setParsedData(data);
      setShowPreview(true);
      if (data.error_rows > 0) {
        toast.warning(`${data.error_rows} row(s) have validation errors`);
      } else {
        toast.success(`${data.valid_rows} row(s) validated successfully`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse file');
    } finally { setParsing(false); }
  };

  const handleConfirmUpload = async () => {
    const propId = selectedPropertyId !== 'all' ? selectedPropertyId : null;
    if (!parsedData || !propId) {
      toast.error('Please select a property before uploading');
      return;
    }
    const validRows = parsedData.rows.filter(r => r.valid);
    if (validRows.length === 0) { toast.error('No valid rows to upload'); return; }

    setConfirming(true);
    try {
      const { data } = await portalApi.post('/api/einvoice/gateway/bookings/confirm-upload', {
        property_id: propId,
        rows: validRows
      }, { headers });
      toast.success(data.message);
      setShowPreview(false);
      setParsedData(null);
      setUploadFile(null);
      // Reset file input
      const fileInput = document.getElementById('excel-file-input');
      if (fileInput) fileInput.value = '';
      await loadBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create bookings');
    } finally { setConfirming(false); }
  };

  // ── Manual Booking Handlers ──

  const handleCreateManualBooking = async () => {
    const propId = selectedPropertyId !== 'all' ? selectedPropertyId : null;
    if (!propId) { toast.error('Select a property first'); return; }
    if (!manualForm.buyer_name) { toast.error('Buyer name is required'); return; }
    if (!manualForm.room_charge || parseFloat(manualForm.room_charge) <= 0) {
      toast.error('Room charge is required'); return;
    }

    setSavingBooking(true);
    try {
      const payload = {
        property_id: propId,
        buyer_type: manualForm.buyer_type,
        buyer_name: manualForm.buyer_name,
        buyer_tin: manualForm.buyer_tin || 'EI00000000010',
        buyer_id_type: manualForm.buyer_id_type,
        buyer_id_value: manualForm.buyer_id_value,
        line_items: []
      };
      if (parseFloat(manualForm.room_charge) > 0)
        payload.line_items.push({ description: 'Room Charge', quantity: 1, unit_price: parseFloat(manualForm.room_charge), tax_rate: 8 });
      if (parseFloat(manualForm.tourism_tax || 0) > 0)
        payload.line_items.push({ description: 'Tourism Tax', quantity: 1, unit_price: parseFloat(manualForm.tourism_tax), tax_rate: 0 });
      if (parseFloat(manualForm.hotel_fee || 0) > 0)
        payload.line_items.push({ description: 'Hotel Fee', quantity: 1, unit_price: parseFloat(manualForm.hotel_fee), tax_rate: 0 });

      await portalApi.post('/api/einvoice/gateway/bookings', payload, { headers });
      toast.success('Booking created with invoice');
      setShowManualForm(false);
      setManualForm({ buyer_type: 'individual', buyer_name: '', buyer_tin: '', buyer_id_type: 'NRIC', buyer_id_value: '', buyer_email: '', room_charge: '', tourism_tax: '', hotel_fee: '' });
      await loadBookings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create booking'); }
    finally { setSavingBooking(false); }
  };

  const totalPages = Math.ceil(totalBookings / limit);

  return (
    <PortalLayout>
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-5" data-testid="portal-bookings">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="bookings-title">Bookings</h1>
            <p className="text-slate-500 dark:text-gray-400 text-sm mt-0.5">Manage manual bookings and bulk Excel uploads</p>
            <p className="text-xs font-medium text-[#4169E1] mt-1" data-testid="viewing-property-label">
              Viewing: {selectedPropertyId === 'all' ? 'All Properties' : (properties.find(p => p.id === selectedPropertyId)?.property_name || selectedPropertyId)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selectedPropertyId} onValueChange={handlePropertyChange}>
              <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="booking-property-filter"><SelectValue placeholder="All Properties" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.property_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleDownloadTemplate}
              className="text-xs h-8 bg-slate-600 hover:bg-slate-700 text-white" data-testid="download-template-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download Template
            </Button>
            <Button size="sm" onClick={() => setShowManualForm(true)}
              className="text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-booking-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Create Booking
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 dark:bg-zinc-900 p-1 rounded-lg w-fit" data-testid="booking-tabs">
          {[{ key: 'manual', label: 'All Bookings', icon: FileText }, { key: 'upload', label: 'Excel Upload', icon: FileSpreadsheet }].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === t.key
                  ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200'
              }`} data-testid={`tab-${t.key}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* ═══ TAB: Excel Upload ═══ */}
        {activeTab === 'upload' && (
          <div className="space-y-5">
            {/* Upload Section */}
            <Card className="border-slate-200/80 dark:border-zinc-800 border-dashed" data-testid="excel-upload-card">
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-4">
                    <Upload className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Upload Bookings from Excel</h3>
                  <p className="text-sm text-slate-500 dark:text-gray-400 mt-1 max-w-md">
                    Upload your bookings using the template. The system will validate data, create draft invoices, and allow you to submit to LHDN.
                  </p>

                  <div className="flex items-center gap-3 mt-5">
                    <Input
                      id="excel-file-input"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="w-[280px] text-sm cursor-pointer file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-600 dark:file:bg-blue-900/20 dark:file:text-blue-400 hover:file:bg-blue-100"
                      data-testid="excel-file-input"
                    />
                    <Button size="sm" onClick={handleParseFile} disabled={!uploadFile || parsing}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9" data-testid="validate-upload-btn">
                      {parsing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                      Validate
                    </Button>
                  </div>

                  {uploadFile && (
                    <p className="text-xs text-slate-400 dark:text-gray-500 mt-2">
                      Selected: <span className="font-medium text-slate-600 dark:text-gray-300">{uploadFile.name}</span>
                    </p>
                  )}
                </div>

                {/* Flow Steps */}
                <div className="mt-6 pt-5 border-t border-slate-200 dark:border-zinc-800">
                  <div className="flex items-center justify-center gap-2 text-xs text-slate-400 dark:text-gray-500">
                    <span className="flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-2.5 py-1 rounded-full font-medium">
                      1. Upload Excel
                    </span>
                    <ArrowRight className="w-3 h-3" />
                    <span className="flex items-center gap-1 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 px-2.5 py-1 rounded-full font-medium">
                      2. Validate & Preview
                    </span>
                    <ArrowRight className="w-3 h-3" />
                    <span className="flex items-center gap-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 px-2.5 py-1 rounded-full font-medium">
                      3. Create Draft Invoices
                    </span>
                    <ArrowRight className="w-3 h-3" />
                    <span className="flex items-center gap-1 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 px-2.5 py-1 rounded-full font-medium">
                      4. Submit to LHDN
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview Results */}
            {parsedData && showPreview && (
              <Card className="border-slate-200/80 dark:border-zinc-800" data-testid="preview-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold text-slate-800 dark:text-white">
                      Upload Preview
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-gray-300 text-xs border-0">
                        {parsedData.total_rows} rows
                      </Badge>
                      <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs border-0">
                        {parsedData.valid_rows} valid
                      </Badge>
                      {parsedData.error_rows > 0 && (
                        <Badge className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs border-0">
                          {parsedData.error_rows} errors
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardDescription className="text-xs">Review the data below. Only valid rows will be imported.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="preview-table">
                      <thead>
                        <tr className="border-y border-slate-200 dark:border-zinc-800 bg-slate-50/80 dark:bg-zinc-900/50">
                          <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase">Row</th>
                          <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase">Invoice #</th>
                          <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase">Date</th>
                          <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase">Buyer</th>
                          <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase">Type</th>
                          <th className="text-right px-3 py-2 text-[11px] font-medium text-slate-500 uppercase">Room</th>
                          <th className="text-right px-3 py-2 text-[11px] font-medium text-slate-500 uppercase">Tax</th>
                          <th className="text-right px-3 py-2 text-[11px] font-medium text-slate-500 uppercase">Fee</th>
                          <th className="text-right px-3 py-2 text-[11px] font-medium text-slate-500 uppercase">Total</th>
                          <th className="text-center px-3 py-2 text-[11px] font-medium text-slate-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedData.rows.map((row, i) => (
                          <tr key={i} className={`border-b border-slate-100 dark:border-zinc-800/50 ${!row.valid ? 'bg-red-50/50 dark:bg-red-900/10' : 'hover:bg-slate-50/50 dark:hover:bg-zinc-900/30'}`}
                            data-testid={`preview-row-${row.row_number}`}>
                            <td className="px-3 py-2 text-xs text-slate-400">{row.row_number}</td>
                            <td className="px-3 py-2 font-mono text-xs text-blue-600 dark:text-blue-400">{row.invoice_number || '-'}</td>
                            <td className="px-3 py-2 text-xs text-slate-600 dark:text-gray-300">{row.invoice_date}</td>
                            <td className="px-3 py-2 text-xs text-slate-700 dark:text-gray-200 max-w-[120px] truncate">{row.buyer_name || '-'}</td>
                            <td className="px-3 py-2 text-xs capitalize text-slate-500">{row.buyer_type}</td>
                            <td className="px-3 py-2 text-xs text-right text-slate-600 dark:text-gray-300">{formatRM(row.room_charge)}</td>
                            <td className="px-3 py-2 text-xs text-right text-slate-600 dark:text-gray-300">{formatRM(row.tourism_tax)}</td>
                            <td className="px-3 py-2 text-xs text-right text-slate-600 dark:text-gray-300">{formatRM(row.hotel_fee)}</td>
                            <td className="px-3 py-2 text-xs text-right font-medium text-slate-800 dark:text-white">{formatRM(row.total_amount)}</td>
                            <td className="px-3 py-2 text-center">
                              {row.valid ? (
                                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                                  <CheckCircle2 className="w-3 h-3" /> Valid
                                </span>
                              ) : (
                                <span className="group relative inline-flex items-center gap-1 text-[10px] text-red-500 dark:text-red-400 font-medium cursor-help">
                                  <XCircle className="w-3 h-3" /> Error
                                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-red-800 text-white text-[10px] rounded whitespace-nowrap hidden group-hover:block z-10">
                                    {row.errors.join(', ')}
                                  </span>
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-zinc-800">
                    <div className="text-xs text-slate-500 dark:text-gray-400">
                      {parsedData.valid_rows > 0 && (
                        <>
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400">{parsedData.valid_rows}</span> valid invoice(s) will be created as <Badge className="bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-[10px] border-0">Draft</Badge>
                        </>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setShowPreview(false); setParsedData(null); }}
                        className="text-xs h-8" data-testid="cancel-upload-btn">
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleConfirmUpload} disabled={confirming || parsedData.valid_rows === 0}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8" data-testid="confirm-upload-btn">
                        {confirming ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                        Create {parsedData.valid_rows} Draft Invoice(s)
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ═══ TAB: All Bookings ═══ */}
        {activeTab === 'manual' && (
          <Card className="border-slate-200/80 dark:border-zinc-800" data-testid="bookings-table-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-semibold text-slate-800 dark:text-white">
                Booking Records
                <span className="text-sm font-normal text-slate-400 ml-2">({totalBookings})</span>
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={loadBookings} className="text-xs h-7">
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-48">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : bookings.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="bookings-table">
                    <thead>
                      <tr className="border-y border-slate-200 dark:border-zinc-800 bg-slate-50/80 dark:bg-zinc-900/50">
                        <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase tracking-wide">Booking ID</th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase tracking-wide">Date</th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase tracking-wide">Buyer</th>
                        {selectedPropertyId === 'all' && <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase tracking-wide">Property</th>}
                        <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase tracking-wide">Type</th>
                        <th className="text-right px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase tracking-wide">Amount</th>
                        <th className="text-left px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase tracking-wide">Source</th>
                        <th className="text-center px-4 py-2.5 text-[11px] font-medium text-slate-500 uppercase tracking-wide">Invoice Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(b => (
                        <tr key={b.id} className="border-b border-slate-100 dark:border-zinc-800/50 hover:bg-slate-50/50 dark:hover:bg-zinc-900/30 transition-colors">
                          <td className="px-4 py-2.5 font-mono text-xs text-blue-600 dark:text-blue-400">{b.id?.substring(0, 8)}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-gray-300">{b.booking_date || '-'}</td>
                          <td className="px-4 py-2.5 text-sm text-slate-700 dark:text-gray-200 max-w-[180px] truncate">{b.buyer_name || '-'}</td>
                          {selectedPropertyId === 'all' && (
                            <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-gray-300">{properties.find(p => p.id === b.property_id)?.property_name || '-'}</td>
                          )}
                          <td className="px-4 py-2.5 text-xs capitalize text-slate-500">{b.buyer_type || '-'}</td>
                          <td className="px-4 py-2.5 text-sm text-right font-medium text-slate-800 dark:text-white">{formatRM(b.total_amount)}</td>
                          <td className="px-4 py-2.5">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">
                              {b.source === 'excel_upload' ? 'Excel' : b.source || 'manual'}
                            </Badge>
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {b.invoice_status === 'invoiced' ? (
                              <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">Invoiced</span>
                            ) : (
                              <span className="text-[10px] font-medium text-slate-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-16">
                  <FileText className="w-10 h-10 mx-auto text-slate-200 dark:text-gray-700 mb-2" />
                  <p className="text-sm font-medium text-slate-600 dark:text-gray-300">No bookings yet</p>
                  <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Create a manual booking or upload from Excel</p>
                </div>
              )}
              {/* Pagination - Always visible */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-zinc-800" data-testid="bookings-pagination">
                <p className="text-xs text-slate-500 dark:text-gray-400">
                  {totalBookings > 0 ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, totalBookings)} of ${totalBookings} bookings` : 'No records'}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)} data-testid="bookings-page-prev">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  {totalPages > 0 && [...Array(Math.min(totalPages, 5))].map((_, i) => {
                    const p = page <= 3 ? i + 1 : page - 2 + i;
                    if (p < 1 || p > totalPages) return null;
                    return (
                      <Button key={p} variant={p === page ? "default" : "outline"} size="sm"
                        className={`h-7 w-7 p-0 text-xs ${p === page ? 'bg-blue-600 text-white' : ''}`}
                        onClick={() => setPage(p)} data-testid={`bookings-page-${p}`}>
                        {p}
                      </Button>
                    );
                  })}
                  <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)} data-testid="bookings-page-next">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Manual Booking Modal ── */}
        <Dialog open={showManualForm} onOpenChange={setShowManualForm}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="manual-booking-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Create Manual Booking</DialogTitle>
              <DialogDescription>Add a walk-in or manual booking with invoice generation</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {savedCustomers.length > 0 && (
                <div className="rounded-lg border border-blue-200 dark:border-blue-900/50 bg-blue-50/60 dark:bg-blue-900/10 p-2.5">
                  <Label className="text-xs text-blue-700 dark:text-blue-300">Load saved customer (auto-fill)</Label>
                  <Select onValueChange={applyCustomer}>
                    <SelectTrigger className="h-9 mt-1" data-testid="manual-load-customer"><SelectValue placeholder="Select a saved customer…" /></SelectTrigger>
                    <SelectContent>
                      {savedCustomers.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}{c.tin ? ` — ${c.tin}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">Buyer Type</Label>
                <Select value={manualForm.buyer_type} onValueChange={v => setManualForm(f => ({ ...f, buyer_type: v }))}>
                  <SelectTrigger className="h-9" data-testid="manual-buyer-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Buyer Name *</Label>
                <Input value={manualForm.buyer_name} onChange={e => setManualForm(f => ({ ...f, buyer_name: e.target.value }))}
                  placeholder="Full name or company name" className="h-9" data-testid="manual-buyer-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">TIN</Label>
                  <Input value={manualForm.buyer_tin} onChange={e => setManualForm(f => ({ ...f, buyer_tin: e.target.value }))}
                    placeholder="EI00000000010" className="h-9" data-testid="manual-buyer-tin" />
                </div>
                <div>
                  <Label className="text-xs">ID Value</Label>
                  <Input value={manualForm.buyer_id_value} onChange={e => setManualForm(f => ({ ...f, buyer_id_value: e.target.value }))}
                    placeholder="NRIC / Passport" className="h-9" data-testid="manual-buyer-id" />
                </div>
              </div>
              <hr className="border-slate-200 dark:border-zinc-700" />
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Room Charge (RM) *</Label>
                  <Input type="number" value={manualForm.room_charge} onChange={e => setManualForm(f => ({ ...f, room_charge: e.target.value }))}
                    placeholder="0.00" className="h-9" data-testid="manual-room-charge" />
                </div>
                <div>
                  <Label className="text-xs">Tourism Tax (RM)</Label>
                  <Input type="number" value={manualForm.tourism_tax} onChange={e => setManualForm(f => ({ ...f, tourism_tax: e.target.value }))}
                    placeholder="0.00" className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">Hotel Fee (RM)</Label>
                  <Input type="number" value={manualForm.hotel_fee} onChange={e => setManualForm(f => ({ ...f, hotel_fee: e.target.value }))}
                    placeholder="0.00" className="h-9" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowManualForm(false)}>Cancel</Button>
              <Button onClick={handleCreateManualBooking} disabled={savingBooking}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-manual-booking-btn">
                {savingBooking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Booking
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PortalLayout>
  );
}
